import { isDeepEqual } from "lib/actions/util";

/**
 * A tool whose state can be captured and restored.
 * Used to serialize the result of using the tool (pins, measurements, painted colors, etc.).
 */
export interface SerializableToolInterface<TState> {
  capture(): TState | undefined;
  restore(state: TState | undefined): void;
  /** Whether a given snapshot represents an applied (non-trivial) state. */
  isStateApplied(state: TState | undefined): boolean;
  /** Whether the current live state matches a given snapshot. */
  isMatchingState(state: TState | undefined): boolean;
}

/**
 * Compare the two tool state snapshots using deep equality.
 *
 * Two "unapplied" states always match, even if their captured shapes differ.
 * Ignores `version` property differences.
 */
export function compareToolState<TState extends { version?: unknown }>(
  current: TState | undefined,
  target: TState | undefined,
  isApplied: (state: TState | undefined) => boolean,
): boolean {
  if (current === target) return true;
  const isCurrentApplied = isApplied(current);
  const isTargetApplied = isApplied(target);
  if (!isCurrentApplied && !isTargetApplied) return true;
  if (isCurrentApplied !== isTargetApplied) return false;
  if (current == null || target == null) return false;
  const { version: _va, ...restA } = current as Record<string, unknown>;
  const { version: _vb, ...restB } = target as Record<string, unknown>;
  return isDeepEqual(restA, restB);
}
